import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { SecureContent } from '../utils/sanitize';
import { getImageUrlForStyle, getImageUrl } from '../utils/imageUtils';

const JointSecretaryMessagePage = () => {
  const [jointSecretaryData, setJointSecretaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchJointSecretaryData = async () => {
      try {
        setLoading(true);
        const response = await apiService.getJointSecretaryMessage();
        setJointSecretaryData(response.data);
      } catch (error) {
        setError('Failed to load joint secretary message');
        console.error('Error fetching joint secretary message:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJointSecretaryData();
  }, []);

  if (loading) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-crimson mb-4"></i>
          <p className="text-gray-600">Loading Joint Secretary's Message...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-exclamation-circle text-4xl text-red-500 mb-4"></i>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <div className="relative bg-slate py-20">
        {jointSecretaryData?.bannerImage && (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: getImageUrlForStyle(jointSecretaryData.bannerImage) }}
          ></div>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-sans font-bold text-white mb-4">
            {jointSecretaryData?.bannerTitle || "Joint Secretary's Message"}
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            {jointSecretaryData?.bannerSubtitle || 'Fostering collaboration and driving institutional progress'}
          </p>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          {/* Main Content Card */}
          <div className="bg-white rounded-xl shadow-soft p-12 md:p-16">
            <div className="grid md:grid-cols-4 gap-12 mb-8">
              {/* Photo */}
              <div className="md:col-span-1">
                <div className="bg-gray-100 rounded-lg aspect-[3/4] flex items-center justify-center overflow-hidden">
                  {jointSecretaryData?.jointSecretaryImage ? (
                    <img 
                      src={getImageUrl(jointSecretaryData.jointSecretaryImage)} 
                      alt={jointSecretaryData.jointSecretaryName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <i className="fas fa-users text-8xl text-gray-400"></i>
                  )}
                </div>
                <div className="mt-6 text-center">
                  <h3 className="font-bold text-xl text-slate">
                    {jointSecretaryData?.jointSecretaryName || 'Mr. Amit Patel'}
                  </h3>
                  <p className="text-base text-gray-500 mt-2">
                    {jointSecretaryData?.jointSecretaryTitle || 'Joint Secretary'}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {jointSecretaryData?.jointSecretaryInstitution || 'FiT Engineering College'}
                  </p>
                </div>
              </div>
            
              {/* Message Content */}
              <div className="md:col-span-3 space-y-6">
                <SecureContent 
                  content={jointSecretaryData?.messageContent || 
                    `<p>Greetings to the FIT Family,</p>
                     <p>As Joint Secretary of FiT Engineering College, I am privileged to be part of an institution...</p>`
                  }
                  className="text-gray-600 leading-relaxed rich-text-content"
                />
                <p className="text-gray-600 leading-relaxed font-semibold mt-8">
                  Sincerely,<br/>
                  <span className="text-crimson text-xl">{jointSecretaryData?.jointSecretaryName || 'Mr. Amit Patel'}</span><br/>
                  <span className="text-base font-normal">
                    {jointSecretaryData?.jointSecretaryTitle || 'Joint Secretary'}, {jointSecretaryData?.jointSecretaryInstitution || 'FiT Engineering College'}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JointSecretaryMessagePage;